/**
 * Quality Assurance Agent
 * Validates listing data quality, checks for completeness, suggests improvements
 */

interface ListingData {
  id: string;
  name: string;
  description?: string;
  location?: { lat: number; lng: number };
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  categories?: string[];
  products?: string[];
  rating?: number;
  reviews?: number;
  hours?: { [key: string]: string };
  certifications?: string[];
  imageCount?: number;
  minOrder?: number;
  shippingOptions?: string[];
  paymentMethods?: string[];
  socialMedia?: { [key: string]: string };
}

interface QualityScore {
  listingId: string;
  overallScore: number;
  completeness: number;
  accuracy: number;
  richness: number;
  issues: QualityIssue[];
  suggestions: string[];
}

interface QualityIssue {
  field: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
}

class QualityAssuranceAgent {
  /**
   * Calculate quality score for a listing
   */
  calculateQualityScore(listing: ListingData): QualityScore {
    const issues: QualityIssue[] = [];
    const suggestions: string[] = [];

    // Check required fields
    this.validateRequiredFields(listing, issues);

    // Check field completeness
    const completeness = this.calculateCompleteness(listing, suggestions);

    // Check data accuracy
    const accuracy = this.checkAccuracy(listing, issues);

    // Check content richness
    const richness = this.calculateRichness(listing, suggestions);

    // Calculate overall score (40% completeness, 30% accuracy, 30% richness)
    const overallScore =
      completeness * 0.4 + accuracy * 0.3 + richness * 0.3;

    return {
      listingId: listing.id,
      overallScore: Math.round(overallScore),
      completeness: Math.round(completeness),
      accuracy: Math.round(accuracy),
      richness: Math.round(richness),
      issues,
      suggestions,
    };
  }

  /**
   * Validate required fields
   */
  private validateRequiredFields(
    listing: ListingData,
    issues: QualityIssue[]
  ): void {
    const requiredFields = [
      { field: 'name', label: 'Business Name' },
      { field: 'location', label: 'Location Coordinates' },
      { field: 'address', label: 'Address' },
      { field: 'phone', label: 'Phone Number' },
      { field: 'email', label: 'Email Address' },
    ];

    requiredFields.forEach(({ field, label }) => {
      const value = (listing as any)[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        issues.push({
          field,
          severity: 'critical',
          message: `${label} is missing`,
        });
      }
    });
  }

  /**
   * Calculate completeness score (0-100)
   */
  private calculateCompleteness(
    listing: ListingData,
    suggestions: string[]
  ): number {
    const totalFields = 15;
    const fields = [
      'name',
      'description',
      'location',
      'address',
      'phone',
      'email',
      'website',
      'categories',
      'products',
      'rating',
      'hours',
      'certifications',
      'imageCount',
      'socialMedia',
      'paymentMethods',
    ];

    let filledFields = 0;
    fields.forEach((field) => {
      const value = (listing as any)[field];
      if (value && (Array.isArray(value) ? value.length > 0 : value !== '')) {
        filledFields++;
      }
    });

    const completeness = (filledFields / totalFields) * 100;

    // Add suggestions for missing important fields
    if (!listing.description) {
      suggestions.push('Add a detailed description of your farm');
    }
    if (!listing.website) {
      suggestions.push('Add your website URL for better online presence');
    }
    if (!listing.categories || listing.categories.length === 0) {
      suggestions.push('Categorize your farm with relevant categories');
    }
    if (!listing.hours) {
      suggestions.push('Add your business hours for customer convenience');
    }
    if (!listing.socialMedia || Object.keys(listing.socialMedia).length === 0) {
      suggestions.push('Link your social media accounts');
    }

    return completeness;
  }

  /**
   * Check data accuracy (0-100)
   */
  private checkAccuracy(
    listing: ListingData,
    issues: QualityIssue[]
  ): number {
    let accuracy = 100;

    // Validate email format
    if (listing.email && !this.isValidEmail(listing.email)) {
      issues.push({
        field: 'email',
        severity: 'warning',
        message: 'Email format appears invalid',
      });
      accuracy -= 15;
    }

    // Validate phone format
    if (listing.phone && !this.isValidPhone(listing.phone)) {
      issues.push({
        field: 'phone',
        severity: 'warning',
        message: 'Phone number format may be invalid',
      });
      accuracy -= 10;
    }

    // Validate location coordinates
    if (listing.location) {
      if (
        !this.isValidCoordinate(listing.location.lat, listing.location.lng)
      ) {
        issues.push({
          field: 'location',
          severity: 'warning',
          message: 'Location coordinates may be invalid',
        });
        accuracy -= 20;
      }
    }

    // Check for suspiciously short description
    if (listing.description && listing.description.length < 20) {
      issues.push({
        field: 'description',
        severity: 'info',
        message: 'Description is very brief - consider expanding',
      });
      accuracy -= 5;
    }

    // Check rating validity
    if (listing.rating && (listing.rating < 0 || listing.rating > 5)) {
      issues.push({
        field: 'rating',
        severity: 'critical',
        message: 'Rating must be between 0 and 5',
      });
      accuracy -= 25;
    }

    return Math.max(0, accuracy);
  }

  /**
   * Calculate content richness (0-100)
   */
  private calculateRichness(
    listing: ListingData,
    suggestions: string[]
  ): number {
    let richness = 0;

    // Description length (0-25 points)
    if (listing.description) {
      const descLength = listing.description.length;
      richness += Math.min(25, Math.floor(descLength / 50));
    }

    // Product diversity (0-20 points)
    if (listing.products && listing.products.length > 0) {
      richness += Math.min(20, listing.products.length * 2);
    }

    // Category diversity (0-15 points)
    if (listing.categories && listing.categories.length > 0) {
      richness += Math.min(15, listing.categories.length * 5);
    }

    // Image count (0-15 points)
    if (listing.imageCount) {
      richness += Math.min(15, listing.imageCount * 2);
    }

    // Certifications (0-10 points)
    if (listing.certifications && listing.certifications.length > 0) {
      richness += Math.min(10, listing.certifications.length * 5);
    }

    // Social media presence (0-15 points)
    if (listing.socialMedia && Object.keys(listing.socialMedia).length > 0) {
      richness += Math.min(15, Object.keys(listing.socialMedia).length * 3);
    }

    if (listing.imageCount === 0 || !listing.imageCount) {
      suggestions.push('Add high-quality photos of your farm and products');
    }

    return Math.min(100, richness);
  }

  /**
   * Validate email format
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate phone format
   */
  private isValidPhone(phone: string): boolean {
    const phoneRegex = /^\d{3}[-.\s]?\d{3}[-.\s]?\d{4}$/;
    return phoneRegex.test(phone) || phone.length >= 10;
  }

  /**
   * Validate coordinates
   */
  private isValidCoordinate(lat: number, lng: number): boolean {
    // Check if coordinates are within Florida bounds (approximately)
    return (
      lat >= 24 &&
      lat <= 30.5 &&
      lng >= -87.5 &&
      lng <= -80
    );
  }

  /**
   * Batch validate multiple listings
   */
  batchValidate(listings: ListingData[]): QualityScore[] {
    return listings.map((listing) => this.calculateQualityScore(listing));
  }

  /**
   * Get listings that need improvement
   */
  getProblematicListings(
    listings: ListingData[],
    minScore: number = 60
  ): { listing: ListingData; score: QualityScore }[] {
    return listings
      .map((listing) => ({
        listing,
        score: this.calculateQualityScore(listing),
      }))
      .filter((item) => item.score.overallScore < minScore)
      .sort((a, b) => a.score.overallScore - b.score.overallScore);
  }

  /**
   * Get summary statistics for multiple listings
   */
  getSummaryStatistics(listings: ListingData[]): {
    totalListings: number;
    averageScore: number;
    averageCompleteness: number;
    averageAccuracy: number;
    averageRichness: number;
    criticalIssues: number;
    warningIssues: number;
  } {
    const scores = listings.map((listing) => this.calculateQualityScore(listing));

    const averageScore =
      scores.reduce((sum, s) => sum + s.overallScore, 0) / scores.length;
    const averageCompleteness =
      scores.reduce((sum, s) => sum + s.completeness, 0) / scores.length;
    const averageAccuracy =
      scores.reduce((sum, s) => sum + s.accuracy, 0) / scores.length;
    const averageRichness =
      scores.reduce((sum, s) => sum + s.richness, 0) / scores.length;

    const criticalIssues = scores.reduce(
      (sum, s) =>
        sum + s.issues.filter((i) => i.severity === 'critical').length,
      0
    );
    const warningIssues = scores.reduce(
      (sum, s) =>
        sum + s.issues.filter((i) => i.severity === 'warning').length,
      0
    );

    return {
      totalListings: listings.length,
      averageScore: Math.round(averageScore),
      averageCompleteness: Math.round(averageCompleteness),
      averageAccuracy: Math.round(averageAccuracy),
      averageRichness: Math.round(averageRichness),
      criticalIssues,
      warningIssues,
    };
  }

  /**
   * Generate improvement report
   */
  generateImprovementReport(listing: ListingData): {
    currentScore: QualityScore;
    potentialScore: QualityScore;
    quickWins: string[];
    priorityActions: string[];
    estimatedImpact: number;
  } {
    const currentScore = this.calculateQualityScore(listing);

    // Create hypothetical improved listing
    const improvedListing = { ...listing };
    if (!improvedListing.description) {
      improvedListing.description = 'A great farm offering fresh local produce.';
    }
    if (!improvedListing.categories || improvedListing.categories.length === 0) {
      improvedListing.categories = ['Vegetables', 'Fruits'];
    }
    if (!improvedListing.website) {
      improvedListing.website = 'www.example.com';
    }
    if (!improvedListing.hours) {
      improvedListing.hours = {
        Monday: '9 AM - 5 PM',
        Saturday: '8 AM - 4 PM',
      };
    }

    const potentialScore = this.calculateQualityScore(improvedListing);
    const estimatedImpact = potentialScore.overallScore - currentScore.overallScore;

    const quickWins = currentScore.suggestions.slice(0, 3);
    const priorityActions = currentScore.issues
      .filter((i) => i.severity === 'critical')
      .map((i) => `Fix: ${i.message}`);

    return {
      currentScore,
      potentialScore,
      quickWins,
      priorityActions,
      estimatedImpact,
    };
  }
}

export default new QualityAssuranceAgent();
